import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, Container, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getAuthToken } from "../../utils/auth";
import { ITEMS_PER_PAGE } from "../../constants/appConstants";
import PaginationBar from "../common/PaginationBar";

const STATUSES = ["SUCCESS", "PENDING", "FAILED", "REFUNDED"];

const STATUS_BADGE = {
    SUCCESS: "success",
    PENDING: "warning",
    FAILED: "danger",
    REFUNDED: "secondary",
};

// ISO 4217 numeric currency codes used by the bank.
const CURRENCY_LABELS = { 934: "TMT", 840: "USD", 978: "EUR" };

// Amounts are stored in the smallest unit (teňňe), so 6000 = 60.00 TMT.
const formatAmount = (amount, currency) => {
    if (amount == null) return "-";
    const label = CURRENCY_LABELS[currency] ?? currency ?? "";
    return `${(amount / 100).toFixed(2)} ${label}`.trim();
};

// Stored as "cardholder|card number|ip" after a successful payment.
const parsePayer = (payerMetadata) => {
    if (!payerMetadata) return null;
    const [cardholder, card, ip] = payerMetadata.split("|");
    return { cardholder, card, ip };
};

const PaymentOrdersManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();

    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [status, setStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const url = new URL(`${baseUrl}/admin/payment-orders`);
            url.searchParams.append("page", currentPage);
            url.searchParams.append("size", ITEMS_PER_PAGE);
            if (appliedSearch.trim()) url.searchParams.append("query", appliedSearch.trim());
            if (status) url.searchParams.append("status", status);

            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const result = await res.json();

            if (result.status) {
                setOrders(result.data || []);
                setTotalPages(result.pagination?.totalPages || 0);
                setTotalElements(result.pagination?.totalElements || 0);
            } else {
                setOrders([]);
            }
        } catch (err) {
            console.error("Error fetching payment orders", err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, currentPage, appliedSearch, status]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        setCurrentPage(0);
    }, [appliedSearch, status]);

    const handleSearch = () => setAppliedSearch(search);
    const handleClear = () => {
        setSearch("");
        setAppliedSearch("");
        setStatus("");
    };

    return (
        <Container className="mt-4">
            <Row className="mb-3 align-items-center g-2">
                <Col md={4}>
                    <h3 className="mb-0">Payment Orders</h3>
                    {!loading && <div className="small text-muted">{totalElements} orders</div>}
                </Col>
                <Col md={3}>
                    <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={5}>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Phone, order ID, promo code, cardholder..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                        <Button variant="primary" onClick={handleSearch}>Search</Button>
                        <Button variant="outline-secondary" onClick={handleClear}>Clear</Button>
                    </InputGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive className="align-middle">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Created</th>
                                <th>User</th>
                                <th>Plan</th>
                                <th className="text-end">Amount</th>
                                <th>Status</th>
                                <th>Promo code</th>
                                <th>Order IDs</th>
                                <th>Payer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.length > 0 ? (
                                orders.map((order) => {
                                    const payer = parsePayer(order.payerMetadata);
                                    return (
                                        <tr key={order.id}>
                                            <td>{order.id}</td>
                                            <td className="small text-nowrap">
                                                {order.createdAt ? new Date(order.createdAt).toLocaleString() : "-"}
                                            </td>
                                            <td className="text-nowrap">
                                                <div>
                                                    {order.userPhone || "-"}
                                                    {order.userDeleted && (
                                                        <Badge bg="danger" className="ms-1">Deleted</Badge>
                                                    )}
                                                </div>
                                                {order.userId && (
                                                    <Link to={`/admin/users/${order.userId}`} className="small">
                                                        User #{order.userId}
                                                    </Link>
                                                )}
                                            </td>
                                            <td>{order.subscriptionType || "-"}</td>
                                            <td className="text-end text-nowrap fw-semibold">
                                                {formatAmount(order.amount, order.currency)}
                                            </td>
                                            <td>
                                                <Badge bg={STATUS_BADGE[order.status] || "secondary"}>
                                                    {order.status}
                                                </Badge>
                                            </td>
                                            <td>{order.referralCode || "-"}</td>
                                            <td className="small">
                                                <div><span className="text-muted">Ours:</span> {order.engTrainerOrderId || "-"}</div>
                                                <div><span className="text-muted">Bank:</span> {order.bankOrderId || "-"}</div>
                                                {order.provider && <div className="text-muted">{order.provider}</div>}
                                            </td>
                                            <td className="small">
                                                {payer ? (
                                                    <>
                                                        <div>{payer.cardholder}</div>
                                                        <div className="text-muted">{payer.card}</div>
                                                        {payer.ip && <div className="text-muted">IP {payer.ip}</div>}
                                                    </>
                                                ) : "-"}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center text-muted">
                                        No payment orders found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </Container>
    );
};

export default PaymentOrdersManagement;
